// @ts-ignore
import { DateUtils } from '@fulcrumapp/fulcrum-core';
import type { MediaType } from '../media-field-type';

type StringOrNumber = string | number;
type NullableStringOrNumber = StringOrNumber | null;

interface Metadata {
  id: string
  size: StringOrNumber
  latitude?: NullableStringOrNumber
  longitude?: NullableStringOrNumber
  altitude?: number | null
  accuracy?: number | null
  direction?: number | null
  width?: number | null
  height?: number | null
  timestamp?: string | null
  duration?: number | null
  orientation?: string | null
}

interface MetadataStream {
  codec_type?: string;
  width?: number | null;
  height?: number | null;
  tags?: {
    rotate?: string | null;
  };
}

export interface MetadataInput {
  access_key: string;
  file_size: StringOrNumber;
  latitude?: NullableStringOrNumber;
  longitude?: NullableStringOrNumber;
  exif?: {
    gps_altitude?: NullableStringOrNumber;
    gps_dop?: NullableStringOrNumber;
    gps_img_direction?: NullableStringOrNumber;
    pixel_x_dimension?: NullableStringOrNumber;
    pixel_y_dimension?: NullableStringOrNumber;
    date_time?: string | null;
  };
  metadata?: {
    format?: {
      tags?: {
        creation_time?: string | null;
      };
      duration?: string | number | null;
    };
    streams?: MetadataStream[];
  };
}

export default class MetadataParser {
  parse = (object: MetadataInput): Metadata => {
    const parsed: Metadata = {
      id: object.access_key,
      size: object.file_size,
    };

    if (object.latitude != null) parsed.latitude = object.latitude;
    if (object.longitude != null) parsed.longitude = object.longitude;

    if (object.exif) {
      parsed.altitude = this.numOrNull(object.exif.gps_altitude);
      parsed.accuracy = this.numOrNull(object.exif.gps_dop);
      parsed.direction = this.numOrNull(object.exif.gps_img_direction);
      parsed.width = this.numOrNull(object.exif.pixel_x_dimension);
      parsed.height = this.numOrNull(object.exif.pixel_y_dimension);
      parsed.timestamp = this.parseTimestamp(object.exif.date_time ?? null);
    }

    if (object.metadata?.format) {
      const creationTime = object.metadata.format?.tags?.creation_time ?? null;
      parsed.timestamp = this.parseTimestamp(creationTime);
      const durationValue = object.metadata.format.duration == null
        ? null
        : String(object.metadata.format.duration);
      parsed.duration = this.numOrNull(durationValue);

      const videoStream = object.metadata?.streams?.find((s) => s.codec_type === 'video');

      if (videoStream) {
        parsed.width = videoStream?.width ?? null;
        parsed.height = videoStream?.height ?? null;
        parsed.orientation = videoStream?.tags?.rotate ?? null;
      }
    }

    return parsed;
  };

  // Coerce a value to a number or null if the value is nullish
  numOrNull = (val?: NullableStringOrNumber): number | null => (val != null
    && !Number.isNaN(+val) ? +val : null);

  // Format a Date object. End result: YYYY/MM/DD HH:MM:SS
  formatDateTime = (dateTime: Date): string => {
    // @ts-ignore
    const formattedDate = DateUtils.formatDate(dateTime).replaceAll('-', '/');
    const formattedTime = DateUtils.formatTimeParts(dateTime.getHours(),
      dateTime.getMinutes(),
      dateTime.getSeconds());
    return `${formattedDate} ${formattedTime}`;
  };

  // Parse a string timestamp into a valid and uniform date time string.
  // End result: YYYY/MM/DD HH:MM:SS OR null if the timestamp is unparseable.
  parseTimestamp = (timestamp: string | null): string | null => {
    if (timestamp == null) return null;

    let createdDateTime = new Date(timestamp);

    // If we fail to get a Date object from timestamp, try to parse the
    // string for a space separated datetime.
    if (!this.isValidDate(createdDateTime)) {
      const [date, fullTime] = timestamp.split(' ');
      createdDateTime = new Date(`${date}T${fullTime}Z`);
    }

    if (!this.isValidDate(createdDateTime)) {
      return null;
    }
    return this.formatDateTime(createdDateTime);
  };

  // Check if a Date object is actually a Date and not `invalid date`
  isValidDate = (datetime: Date | null): boolean => datetime != null
    && !Number.isNaN(datetime.getTime());
}

const LEGACY_MEDIA_RESPONSE_KEYS: Partial<Record<MediaType, string>> = {
  photo: 'photo',
  video: 'video',
  audio: 'audio',
  sketch: 'sketch',
  signature: 'signature',
  attachment: 'attachment',
};

export function resolveLegacySourceMetadata(
  mediaType: MediaType | undefined,
  attachmentData: Record<string, unknown>,
): Record<string, unknown> & Partial<MetadataInput> {
  if (!mediaType) return attachmentData;

  const nestedKey = LEGACY_MEDIA_RESPONSE_KEYS[mediaType];
  if (!nestedKey) return attachmentData;

  const nestedValue = attachmentData[nestedKey];
  if (nestedValue && typeof nestedValue === 'object') {
    return nestedValue as Record<string, unknown> & Partial<MetadataInput>;
  }

  return attachmentData;
}

export function buildEventMetadata(
  mediaType: MediaType | undefined,
  accessKey: string,
  parsedLegacyMetadata: Metadata,
  attachmentData: Record<string, unknown>,
): Record<string, unknown> {
  const attachmentSize = toNumber(attachmentData.size);
  const parsedSize = toNumber(parsedLegacyMetadata.size);
  const fileName = typeof attachmentData.name === 'string' ? attachmentData.name : undefined;

  if (mediaType === 'attachment') {
    return {
      ...attachmentData,
      ...parsedLegacyMetadata,
      id: accessKey,
      size: attachmentSize ?? attachmentData.size,
      fileName,
    };
  }

  return {
    ...parsedLegacyMetadata,
    id: accessKey,
    size: attachmentSize ?? parsedSize ?? parsedLegacyMetadata.size,
    ...(fileName ? { fileName } : {}),
  };
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const coercedValue = Number(value);
    return Number.isNaN(coercedValue) ? undefined : coercedValue;
  }
  return undefined;
}
